/**
 * First-touch landing attribution (2026-10-03).
 *
 * Every Perfect Storm registration reached FLOW and the funnel log with no
 * source at all: the UTMs were read from the URL at submit time only, so a
 * visitor who landed with tags and then moved between pages (explainer →
 * /webinar) lost them, and links without tags (ads, organic posts) carried
 * nothing to fall back on. This captures where the visitor came from the
 * moment the app boots and keeps it for the session's later form submits.
 *
 * Stored in sessionStorage for the browser session so the first touch
 * survives client-side and full-page navigation. The existing 30-day
 * localStorage record remains as a fallback for the Perfect Storm funnel.
 */
const STORAGE_KEY = 've-landing-attribution';
export const SESSION_STORAGE_KEY = 've-session-landing-attribution';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'] as const;

export type LandingAttribution = {
  /** utm_* values without the prefix: { source, medium, ... }. */
  utm: Record<string, string>;
  /** Funnel variant (?v=softer|infant|severe). */
  variant?: string;
  /** Funnel src tag (?src=manychat, ...). */
  src?: string;
  /** External referrer hostname (never the full URL). */
  referrer?: string;
  /** Meta click id present (paid or organic Facebook/Instagram click). */
  fbclid?: boolean;
  /** Google Ads click id present. */
  gclid?: boolean;
  /** First page path of the visit. */
  landing?: string;
  /** ISO time of capture. */
  at: string;
};

function clip(value: string | null | undefined, max = 100): string | undefined {
  const v = value?.trim();
  return v ? v.slice(0, max) : undefined;
}

function externalReferrerHost(referrer: string, ownHost: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    const bare = (h: string) => h.replace(/^www\./, '');
    return bare(host) === bare(ownHost.toLowerCase()) ? undefined : host;
  } catch {
    return undefined;
  }
}

/** Reads the signals present on this page load (no storage). */
export function readCurrentTouch(
  search: string,
  referrer: string,
  ownHost: string,
  path: string,
  now = new Date(),
): LandingAttribution {
  const params = new URLSearchParams(search);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = clip(params.get(`utm_${key}`));
    if (value) utm[key] = value;
  }
  const touch: LandingAttribution = { utm, at: now.toISOString(), landing: clip(path, 200) };
  const variant = clip(params.get('v'), 40);
  if (variant) touch.variant = variant;
  const src = clip(params.get('src'));
  if (src) touch.src = src;
  const ref = externalReferrerHost(referrer, ownHost);
  if (ref) touch.referrer = ref;
  if (params.has('fbclid')) touch.fbclid = true;
  if (params.has('gclid')) touch.gclid = true;
  return touch;
}

function hasSignal(t: LandingAttribution): boolean {
  return Boolean(Object.keys(t.utm).length || t.src || t.referrer || t.fbclid || t.gclid);
}

function loadFromStorage(
  storage: Storage,
  key: string,
  now: number,
  maxAgeMs?: number,
): LandingAttribution | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LandingAttribution;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.at !== 'string') return null;
    if (maxAgeMs !== undefined && now - Date.parse(parsed.at) > maxAgeMs) return null;
    return { ...parsed, utm: parsed.utm ?? {} };
  } catch {
    return null;
  }
}

function loadLocal(now: number): LandingAttribution | null {
  return loadFromStorage(localStorage, STORAGE_KEY, now, MAX_AGE_MS);
}

function loadSession(now: number): LandingAttribution | null {
  return loadFromStorage(sessionStorage, SESSION_STORAGE_KEY, now);
}

/** Call once at app boot, before any client-side navigation. */
export function captureLandingAttribution(): void {
  if (typeof window === 'undefined') return;
  const current = readCurrentTouch(
    window.location.search,
    document.referrer,
    window.location.hostname,
    window.location.pathname,
  );
  const now = Date.now();
  // A browser session's first page wins, including an empty-UTM/direct touch.
  // That keeps a later tagged internal URL from rewriting the true first touch.
  if (!loadSession(now)) {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(current));
    } catch {
      /* storage unavailable: getLandingAttribution falls back to URL/local */
    }
  }

  const next = nextStoredTouch(loadLocal(now), current);
  if (!next) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: getLandingAttribution falls back to the URL */
  }
}

/** What to store after this page load, or null to leave storage alone. */
export function nextStoredTouch(
  stored: LandingAttribution | null,
  current: LandingAttribution,
): LandingAttribution | null {
  if (Object.keys(current.utm).length) return current;
  if (!stored) return hasSignal(current) || current.variant ? current : null;
  if (!hasSignal(stored) && hasSignal(current)) return current;
  if (!stored.variant && current.variant) return { ...stored, variant: current.variant };
  return null;
}

/**
 * Attribution for a form submit: the current URL's UTMs win, then the stored
 * first touch, then an inferred source from the referrer / click id so a
 * Facebook or Instagram visit without tags is still labelled.
 */
export function getLandingAttribution(): LandingAttribution {
  const current = readCurrentTouch(
    window.location.search,
    document.referrer,
    window.location.hostname,
    window.location.pathname,
  );
  const now = Date.now();
  const session = loadSession(now);
  if (session) return session;
  const stored = loadLocal(now);
  if (Object.keys(current.utm).length || !stored) return current;
  return { ...stored, variant: stored.variant ?? current.variant };
}

/** UTM fields for the FLOW intake: explicit tags, else inferred from referrer/click id. */
export function utmForSubmit(a: LandingAttribution): Record<string, string> {
  if (Object.keys(a.utm).length) return a.utm;
  const ref = a.referrer ?? '';
  const source = /instagram/.test(ref)
    ? 'instagram'
    : /facebook|fb\.com|fb\.me/.test(ref) || a.fbclid
      ? 'facebook'
      : /google\./.test(ref) || a.gclid
        ? 'google'
        : ref || undefined;
  if (!source) return {};
  return { source, medium: a.gclid ? 'cpc' : 'referral-inferred' };
}

/** Flat source fields used by the Perfect Storm and condition-guide forms. */
export function attributionFields(a: LandingAttribution): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const [key, value] of Object.entries(utmForSubmit(a))) fields[`utm_${key}`] = value;
  if (a.variant) fields.v = a.variant;
  if (a.src) fields.src = a.src;
  if (a.referrer) fields.ref = a.referrer;
  if (a.fbclid) fields.fbclid = '1';
  if (a.gclid) fields.gclid = '1';
  if (a.landing) fields.landing = a.landing;
  return fields;
}
